import * as React from 'react';
import React__default from 'react';
import { useClasser, ClasserProvider } from '@code-hike/classer';
import { Swap } from 'server-side-media-queries-for-react';
import { Scroller, Step } from '@code-hike/scroller';
import { SandpackProvider, useCodeSandboxLink, SandpackPreview, useLoadingOverlayState } from '@codesandbox/sandpack-react';
import { MiniEditor, mdxToStep } from '@code-hike/mini-editor';
import { MiniBrowser } from '@code-hike/mini-browser';
/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var _assign = function __assign() {
  _assign = Object.assign || function __assign(t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];

      for (var p in s) {
        if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
      }
    }

    return t;
  };

  return _assign.apply(this, arguments);
};

function __rest(s, e) {
  var t = {};

  for (var p in s) {
    if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];
  }

  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
}

function __read(o, n) {
  var m = typeof Symbol === "function" && o[Symbol.iterator];
  if (!m) return o;
  var i = m.call(o),
      r,
      ar = [],
      e;

  try {
    while ((n === void 0 || n-- > 0) && !(r = i.next()).done) {
      ar.push(r.value);
    }
  } catch (error) {
    e = {
      error: error
    };
  } finally {
    try {
      if (r && !r.done && (m = i["return"])) m.call(i);
    } finally {
      if (e) throw e.error;
    }
  }

  return ar;
}

var HikeContext = React__default.createContext(null);

function HikeProvider(_a) {
  var value = _a.value,
      children = _a.children;
  return React__default.createElement(HikeContext.Provider, {
    value: value
  }, children);
}

function useHikeContext() {
  return React__default.useContext(HikeContext);
}

function useFluidContext() {
  return React__default.useContext(HikeContext);
}

var StepContext = React__default.createContext(null);

function ContentColumn(_a) {
  var steps = _a.steps,
      onStepChange = _a.onStepChange;
  var c = useClasser("ch");
  var contentSteps = steps.map(function (s) {
    return s.content;
  });
  return React__default.createElement("div", {
    className: c("hike-content")
  }, React__default.createElement(Scroller, {
    onStepChange: onStepChange
  }, contentSteps.map(function (children, index) {
    return React__default.createElement(StepContext.Provider, {
      value: {
        stepIndex: index,
        editorStep: steps[index].editorProps.contentProps
      },
      key: index
    }, React__default.createElement(StepContent, {
      children: children,
      stepIndex: index
    }));
  })));
}

function StepContent(_a) {
  var _b;

  var children = _a.children,
      stepIndex = _a.stepIndex;
  var c = useClasser("ch-hike-step");

  var _c = useFluidContext(),
      dispatch = _c.dispatch,
      hikeState = _c.hikeState;

  var focusStepIndex = (_b = hikeState.focusStepIndex) !== null && _b !== void 0 ? _b : hikeState.scrollStepIndex;
  var isOn = stepIndex === focusStepIndex;
  return React__default.createElement(Step, {
    as: "div",
    index: stepIndex,
    onClick: function onClick() {
      return dispatch({
        type: "set-focus",
        stepIndex: stepIndex,
        editorStep: null
      });
    },
    className: c("", isOn ? "focused" : "unfocused")
  }, stepIndex > 0 && React__default.createElement("div", {
    className: c("gap")
  }), React__default.createElement("div", {
    className: c("content", isOn ? "content-focused" : "content-unfocused")
  }, children));
}

function useStepData() {
  return React__default.useContext(StepContext);
}

var DemoContext = React__default.createContext(null);

function DemoProvider(_a) {
  var children = _a.children,
      editorProps = _a.editorProps,
      previewProps = _a.previewProps,
      previewPreset = _a.previewPreset;
  var sandpackProps = useSandpackProps(previewPreset, editorProps);
  var previewAndEditorProps = React__default.useMemo(function () {
    return {
      previewProps: previewProps,
      editorProps: editorProps
    };
  }, [previewProps, editorProps]);
  return React__default.createElement(SandpackProvider, _assign({}, sandpackProps), React__default.createElement(DemoContext.Provider, {
    value: previewAndEditorProps
  }, children));
}

function useSandpackProps(previewPreset, editorProps) {
  var _a, _b; // TODO useMemo


  var files = _assign({}, (_a = previewPreset === null || previewPreset === void 0 ? void 0 : previewPreset.customSetup) === null || _a === void 0 ? void 0 : _a.files);

  var codeFiles = ((_b = editorProps === null || editorProps === void 0 ? void 0 : editorProps.contentProps) === null || _b === void 0 ? void 0 : _b.files) || [];
  codeFiles.forEach(function (file) {
    files["/" + file.name] = {
      code: file.code
    };
  });
  return _assign(_assign({
    recompileMode: "immediate"
  }, previewPreset), {
    customSetup: _assign(_assign({}, previewPreset === null || previewPreset === void 0 ? void 0 : previewPreset.customSetup), {
      files: files
    })
  });
}

function useEditorProps() {
  return React__default.useContext(DemoContext).editorProps;
}

function usePreviewProps() {
  return React__default.useContext(DemoContext).previewProps;
}

function Editor(_a) {
  var contentProps = _a.contentProps,
      codeProps = _a.codeProps,
      frameProps = _a.frameProps,
      springConfig = _a.springConfig;

  var finalFrameProps = _assign(_assign({
    button: React.createElement(CodeSandboxIcon, {
      url: useCodeSandboxLink()
    })
  }, frameProps), {
    style: _assign({
      height: "100%"
    }, frameProps === null || frameProps === void 0 ? void 0 : frameProps.style)
  });

  var finalCodeProps = _assign({
    minColumns: 46
  }, codeProps);

  return React.createElement(MiniEditor, _assign({}, contentProps, {
    frameProps: finalFrameProps,
    codeProps: finalCodeProps,
    springConfig: springConfig
  }));
}

function CodeSandboxIcon(_a) {
  var url = _a.url;
  return React.createElement("a", {
    style: {
      margin: "0 1em 0 1em",
      color: "inherit"
    },
    title: "Open in CodeSandbox",
    href: url,
    target: "_blank",
    rel: "noopener noreferrer"
  }, React.createElement("svg", {
    xmlns: "http://www.w3.org/2000/svg",
    height: "1.3em",
    width: "1.3em",
    stroke: "currentColor",
    fill: "currentColor",
    viewBox: "0 0 512 512",
    style: {
      display: "block"
    }
  }, React.createElement("path", {
    d: "M234.4 452V267.5L75.6 176.1v105.2l72.7 42.2v79.1l86.1 49.4zm41.2 1.1l87.6-50.5v-81l73.2-42.4V175.3l-160.8 92.8v185zm139.6-313.2l-84.5-49-74.2 43.1-74.8-43.1-85.3 49.6 159.1 91.6 159.7-92.2zM34.4 384.7V129L256 0l221.6 128.4v255.9L256 512 34.4 384.7z"
  })));
}

function Preview(props) {
  var link = useCodeSandboxLink();
  var preview = React.useMemo(function () {
    return React.createElement(React.Fragment, null, React.createElement(LoadingOverlay, null), React.createElement(SandpackPreview, {
      showNavigator: false,
      showRefreshButton: false,
      showOpenInCodeSandbox: false,
      customStyle: {
        minHeight: "unset",
        height: "100%",
        border: 0,
        margin: 0
      }
    }));
  }, [link]);
  return React.createElement(MiniBrowser, _assign({
    url: "",
    loadUrl: link,
    transition: "slide"
  }, props, {
    children: preview
  }));
}

function LoadingOverlay() {
  var c = useClasser("ch-hike");
  var loadingOverlayState = useLoadingOverlayState();
  return loadingOverlayState === "visible" ? React.createElement("div", {
    className: c("loading")
  }, React.createElement("div", {
    className: c("loading-cubes")
  }, React.createElement("div", null), React.createElement("div", null), React.createElement("div", null), React.createElement("div", null))) : null;
}

function StickerColumn(_a) {
  var editorProps = _a.editorProps,
      previewProps = _a.previewProps,
      previewPreset = _a.previewPreset,
      noPreview = _a.noPreview;
  var c = useClasser("ch");
  return React__default.createElement("aside", {
    className: c("hike-sticker-column")
  }, React__default.createElement("div", {
    className: c("hike-sticker")
  }, React__default.createElement(DemoProvider, {
    editorProps: editorProps,
    previewProps: previewProps,
    previewPreset: previewPreset
  }, React__default.createElement("div", {
    className: c("hike-editor")
  }, React__default.createElement(Editor, _assign({}, editorProps))), noPreview || React__default.createElement("div", {
    className: c("hike-preview")
  }, React__default.createElement(Preview, _assign({}, previewProps))))));
}

function FluidLayout(_a) {
  var _b;

  var steps = _a.steps,
      _c = _a.noPreview,
      noPreview = _c === void 0 ? false : _c;
  var c = useClasser("ch");

  var _d = __read(React__default.useReducer(reducer, initialState), 2),
      state = _d[0],
      dispatch = _d[1];

  var focusStepIndex = (_b = state.focusStepIndex) !== null && _b !== void 0 ? _b : state.scrollStepIndex;
  var focusStep = steps[focusStepIndex];

  var editorProps = _assign(_assign({}, focusStep.editorProps), {
    contentProps: state.focusEditorStep || focusStep.editorProps.contentProps
  });

  var previewProps = focusStep.previewProps,
      previewPreset = focusStep.previewPreset;

  var onStepChange = function onStepChange(newIndex) {
    return dispatch({
      type: "change-step",
      newIndex: newIndex
    });
  };

  return React__default.createElement(HikeProvider, {
    value: {
      layout: "fluid",
      hikeState: state,
      dispatch: dispatch
    }
  }, React__default.createElement("section", {
    className: c("hike", "hike-fluid")
  }, React__default.createElement(ContentColumn, {
    steps: steps,
    onStepChange: onStepChange
  }), React__default.createElement(StickerColumn, {
    previewProps: previewProps,
    previewPreset: previewPreset,
    editorProps: editorProps,
    noPreview: noPreview
  })));
}

var initialState = {
  scrollStepIndex: 0,
  focusStepIndex: null,
  focusEditorStep: null
};

function reducer(state, action) {
  switch (action.type) {
    case "change-step":
      return {
        scrollStepIndex: action.newIndex,
        focusStepIndex: null,
        focusEditorStep: null
      };

    case "set-focus":
      return _assign(_assign({}, state), {
        focusStepIndex: action.stepIndex,
        focusEditorStep: action.editorStep
      });

    case "reset-focus":
      return _assign(_assign({}, state), {
        focusStepIndex: null,
        focusEditorStep: null
      });

    default:
      throw new Error();
  }
}

function FixedLayout(_a) {
  var steps = _a.steps;
  var c = useClasser("ch");
  return React__default.createElement(HikeProvider, {
    value: {
      layout: "fixed"
    }
  }, React__default.createElement("section", {
    className: c("hike", "hike-fixed")
  }, steps.map(function (step, index) {
    return React__default.createElement(DemoProvider, {
      editorProps: step.editorProps,
      previewProps: step.previewProps,
      previewPreset: step.previewPreset,
      key: index
    }, React__default.createElement("div", {
      className: c("hike-step")
    }, step.content));
  })));
}

function CodeSlot(props) {
  var layout = useHikeContext().layout;
  return layout === "fixed" ? React__default.createElement(CodeSlotContent, _assign({}, props)) : null;
}

function CodeSlotContent(_a) {
  var style = _a.style,
      slotProps = __rest(_a, ["style"]);

  var c = useClasser("ch-hike");
  var stepEditorProps = useEditorProps();

  var props = _assign(_assign({}, stepEditorProps), slotProps);

  return React__default.createElement("div", {
    className: c("step-code"),
    style: style
  }, React__default.createElement(Editor, _assign({}, props)));
}

function PreviewSlot(props) {
  var layout = useHikeContext().layout;
  return layout === "fixed" ? React__default.createElement(PreviewSlotContent, _assign({}, props)) : null;
}

function PreviewSlotContent(_a) {
  var style = _a.style,
      slotProps = __rest(_a, ["style"]);

  var c = useClasser("ch-hike");
  var stepPreviewProps = usePreviewProps();

  var props = _assign(_assign({}, stepPreviewProps), slotProps);

  return React__default.createElement("div", {
    className: c("step-preview"),
    style: style
  }, React__default.createElement(Preview, _assign({}, props)));
}

var StepHead = function StepHead() {
  return null;
};

function Hike(_a) {
  var classes = _a.classes,
      children = _a.children,
      editorProps = _a.editorProps,
      previewProps = _a.previewProps,
      preset = _a.preset,
      config = _a.config;
  var steps = useStepsFromChildren({
    children: children,
    editorProps: editorProps,
    previewProps: previewProps,
    preset: preset,
    defaultFileName: config === null || config === void 0 ? void 0 : config.defaultFileName
  });
  return React.createElement(HikeWithSteps, {
    steps: steps,
    classes: classes,
    config: config
  });
}

function HikeWithSteps(_a) {
  var classes = _a.classes,
      steps = _a.steps,
      config = _a.config;
  return React.createElement(ClasserProvider, {
    classes: classes
  }, React.createElement(Swap // TODO use config.breakpoint
  , {
    // TODO use config.breakpoint
    match: [["(min-width: 920px)", React.createElement(FluidLayout, {
      steps: steps,
      noPreview: (config === null || config === void 0 ? void 0 : config.noPreview) || false
    })], ["default", React.createElement(FixedLayout, {
      steps: steps
    })]]
  }));
}

function useStepsFromChildren(_a) {
  // Assumes a step head like:
  // <StepHead codeProps={{minColumns: 20}} previewProps={{zoom: 1}} editorFrameProps={{width: 200}} >
  var _b, _c, _d;

  var children = _a.children,
      _e = _a.editorProps,
      editorProps = _e === void 0 ? {} : _e,
      _f = _a.previewProps,
      previewProps = _f === void 0 ? {} : _f,
      _g = _a.preset,
      preset = _g === void 0 ? {} : _g,
      _h = _a.defaultFileName,
      defaultFileName = _h === void 0 ? "App.js" : _h;
  var kids = React.Children.toArray(children);
  var prevEditorStep = undefined;
  var steps = [];

  for (var i = 0; i < kids.length;) {
    var headKid = kids[i];
    var contentKids = [];
    i++;

    while (i < kids.length && ((_c = (_b = kids[i]) === null || _b === void 0 ? void 0 : _b.props) === null || _c === void 0 ? void 0 : _c.mdxType) !== "StepHead") {
      contentKids.push(kids[i]);
      i++;
    }

    var editorStep = mdxToStep(headKid, prevEditorStep, {
      defaultFileName: defaultFileName
    });
    prevEditorStep = editorStep;
    var headProps = (_d = headKid) === null || _d === void 0 ? void 0 : _d.props;
    var stepPreviewProps = headProps === null || headProps === void 0 ? void 0 : headProps.previewProps;
    var stepCodeProps = headProps === null || headProps === void 0 ? void 0 : headProps.codeProps;
    var stepFrameProps = headProps === null || headProps === void 0 ? void 0 : headProps.editorFrameProps;
    steps.push({
      content: React.createElement(React.Fragment, {
        children: contentKids
      }),
      editorProps: {
        contentProps: editorStep,
        codeProps: _assign(_assign({}, editorProps === null || editorProps === void 0 ? void 0 : editorProps.codeProps), stepCodeProps),
        frameProps: _assign(_assign({}, editorProps === null || editorProps === void 0 ? void 0 : editorProps.frameProps), stepFrameProps),
        springConfig: editorProps === null || editorProps === void 0 ? void 0 : editorProps.springConfig
      },
      previewPreset: preset,
      previewProps: _assign(_assign({}, previewProps), stepPreviewProps)
    });
  }

  return steps;
}

function Focus(_a) {
  var children = _a.children,
      props = __rest(_a, ["children"]);

  var hikeContext = useHikeContext();
  return hikeContext.layout === "fixed" ? children : React__default.createElement(FocusButton, _assign({
    context: hikeContext,
    children: children
  }, props));
}

function FocusButton(_a) {
  var children = _a.children,
      focus = _a.on,
      file = _a.file,
      context = _a.context;
  var c = useClasser("ch-hike");
  var dispatch = context.dispatch,
      hikeState = context.hikeState;

  var _b = __read(useEditorStep(file, focus), 2),
      stepIndex = _b[0],
      newEditorStep = _b[1];

  var oldEditorStep = hikeState.focusEditorStep;
  var isFocused = newEditorStep === oldEditorStep;
  return React__default.createElement("button", {
    className: c("focus", isFocused ? "focus-active" : "focus-inactive"),
    title: "Show code",
    onClick: function onClick(e) {
      isFocused ? dispatch({
        type: "reset-focus"
      }) : dispatch({
        type: "set-focus",
        stepIndex: stepIndex,
        editorStep: newEditorStep
      });
      e.stopPropagation();
    }
  }, children, React__default.createElement(Icon, {
    isFocused: isFocused
  }));
}

function Icon(_a) {
  var isFocused = _a.isFocused;
  var c = useClasser("ch-hike");
  return React__default.createElement("svg", {
    fill: "none",
    stroke: "currentColor",
    className: c("focus-icon"),
    viewBox: "0 0 24 24",
    "aria-hidden": "true",
    focusable: "false",
    xmlns: "http://www.w3.org/2000/svg"
  }, React__default.createElement("path", {
    strokeLinecap: "round",
    strokeLinejoin: "round",
    strokeWidth: 2,
    d: isFocused ? "M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7" : "M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"
  }));
}

function useEditorStep(file, focus) {
  // we merge the editor state from the step with the changes
  // requested by the Focus
  var _a = useStepData(),
      stepIndex = _a.stepIndex,
      editorStep = _a.editorStep;

  return React__default.useMemo(function () {
    var _a;

    var fileName = file || editorStep.northPanel.active;
    var fileIndex = editorStep.files.findIndex(function (f) {
      return f.name === fileName;
    });
    var newFiles = editorStep.files.slice();
    newFiles[fileIndex] = _assign(_assign({}, newFiles[fileIndex]), {
      focus: focus
    });

    var newEditorStep = _assign(_assign({}, editorStep), {
      files: newFiles
    });

    var isInSouthPanel = (_a = editorStep.southPanel) === null || _a === void 0 ? void 0 : _a.tabs.includes(fileName);

    if (isInSouthPanel) {
      newEditorStep.southPanel = _assign(_assign({}, newEditorStep.southPanel), {
        active: fileName
      });
    } else {
      newEditorStep.northPanel = _assign(_assign({}, newEditorStep.northPanel), {
        active: fileName
      });
    }

    return [stepIndex, newEditorStep];
  }, [file, focus]);
}

function withFocusHandler(type) {
  if (type === void 0) {
    type = "a";
  }

  function AnchorWithFocus(props) {
    return React__default.createElement(AnchorOrFocus, _assign({
      type: type
    }, props));
  }

  return AnchorWithFocus;
}

function AnchorOrFocus(_a) {
  var _b = _a.type,
      type = _b === void 0 ? "a" : _b,
      href = _a.href,
      props = __rest(_a, ["type", "href"]);

  if (!(href === null || href === void 0 ? void 0 : href.startsWith("focus://"))) {
    return React__default.createElement(type, _assign({
      href: href
    }, props));
  }

  var _c = __read(decodeURI(href).substr("focus://".length).split("#"), 2),
      firstPart = _c[0],
      secondPart = _c[1];

  var hasFile = Boolean(secondPart);
  return hasFile ? React__default.createElement(Focus, _assign({
    on: secondPart,
    file: firstPart
  }, props)) : React__default.createElement(Focus, _assign({
    on: firstPart
  }, props));
}

export { AnchorOrFocus, CodeSlot, Focus, Hike, HikeWithSteps, PreviewSlot, StepHead, withFocusHandler };